// Centralized API helpers for Lift Admin frontend - Stations
// This file aggregates all REST endpoints in a single place so other features can import a typed helper instead
// of sprinkling axios calls throughout the codebase.

import apiClient from '@lift-admin/utils/apiClient';
import { Toastify } from 'lift-ui';
import { getUserId } from '@lift-admin/utils';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import { diffFields, type FieldSpec, type LogChange } from '@lift-admin/utils/logChangeFormatter';

const STATION_FIELD_SPECS: FieldSpec[] = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'default_model', label: 'Model' },
  { key: 'max_tokens', label: 'Max Tokens' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'is_active', label: 'Active', type: 'boolean', boolStyle: 'yesNo' },
  { key: 'system_prompt', label: 'System Prompt' },
  { key: 'allowed_tools', label: 'Allowed Tools', type: 'list' },
];

// Long values (e.g. system prompts) are truncated for readable log entries.
const truncateChange = (change: LogChange): LogChange => {
  const clip = (s?: string) => (typeof s === 'string' && s.length > 160 ? `${s.slice(0, 160)}…` : s);
  return { ...change, from: clip(change.from), to: clip(change.to) };
};

// ---------------  Environment Switch --------------------------------------
// Toggle this flag to switch all API calls between localhost (dev) and the
// production reverse-proxied "/buddies" prefix.  When `USE_LOCAL_BACKEND` is
// true the API_BASE becomes "http://localhost:8081" (make sure the backend is
// running locally).  When false it defaults to using the relative "/buddies"
// path so requests are proxied by the frontend server.

const USE_LOCAL_BACKEND = false; // <-- set to true when developing locally
const API_BASE = USE_LOCAL_BACKEND ? 'http://localhost:8081' : '/buddies';

/** Build the final URL by concatenating the base with the path */
const apiUrl = (path: string) => `${API_BASE}${path}`;

// ----------  Stations  ------------------------------------------------
// TODO: add more endpoint helpers as they are required by the product modules.

export interface Station {
  buddy_id: string; // backend uses buddy_id as primary identifier
  agent_id?: string; // legacy field, use buddy_id instead
  name: string;
  description?: string;
  available_tools?: string[];
  default_model?: string;
  max_tokens?: number;
  temperature?: number | string;
  is_active: boolean;
  meta_config?: Record<string, unknown>;
  policies?: Record<string, unknown>;
  system_prompt?: string;
  version?: number | string;
  created_at?: string;
  updated_at?: string;
  parent_buddy_id?: string | null;
  sort_order?: number; // Sort order for the station
}

export interface StationWithChildren extends Station {
  children?: StationWithChildren[];
}

export interface StationFile {
  /** Optional local identifier used by UI; not returned by backend */
  id?: string;
  buddy_id: string; // backend uses buddy_id as primary identifier
  agent_id?: string; // legacy field, use buddy_id instead  
  file_id: string;
  file_name: string;
  version: number;
  is_active: boolean;
  uploaded_at?: string;
  created_at?: string;
  updated_at?: string;
  vector_store_id?: string | null;
  user_id?: number;
  parent_file_id?: string | null;
  file_size?: number;
  file_metadata?: Record<string, unknown>;
  children?: StationFile[];
}

export interface CreateStationPayload {
  name: string;
  description?: string;
  allowed_tools: string[];
  default_model: string;
  max_tokens: number;
  temperature: number;
  is_active: boolean;
  system_prompt: string;
  policies: Record<string, unknown>;
  meta_config?: Record<string, unknown>;
  version?: number;
  version_note?: Record<string, unknown>;
  parent_buddy_id?: string; // For version creation flow
  avatarFile?: File; // Optional avatar file for upload
}

export type UpdateStationPayload = Partial<CreateStationPayload> & {
  version_note?: Record<string, unknown>;
};

export interface CreateStationVersionPayload extends Omit<CreateStationPayload, 'version'> {
  parent_agent_id: string;
  avatarFile?: File; // Optional avatar file for upload
}

/**
 * Fetch all parent buddies available to the admin user.
 * API Docs:  GET /api/admin/buddies – List all parent buddies with config summary
 */
export async function getStations(): Promise<Station[]> {
  try {
    const response = await apiClient.get<Station[]>(apiUrl('/api/admin/buddies'));
    return response.data;
  } catch (error) {
    console.error('Error fetching stations:', error);
    Toastify('error', 'Failed to fetch stations');
    throw error;
  }
}

/**
 * Fetch all buddies with their version history (nested children).
 * API Docs: GET /api/admin/all-buddies
 */
export async function getAllStationsWithVersions(): Promise<StationWithChildren[]> {
  try {
    const response = await apiClient.get<StationWithChildren[]>(apiUrl('/api/admin/all-buddies'));
    return response.data;
  } catch (error) {
    console.error('Error fetching stations with versions:', error);
    Toastify('error', 'Failed to fetch stations with versions');
    throw error;
  }
}

/**
 * List files for a given station.
 * API Docs: GET /api/admin/files?station_id={id}&include_inactive=false
 */
export async function getFilesForStation(stationId: string, includeInactive = false): Promise<StationFile[]> {
  try {
    const { data } = await apiClient.get<StationFile[]>(apiUrl('/api/admin/files'), {
      params: {
        buddy_id: stationId,
        include_inactive: includeInactive,
      },
    });
    return data;
  } catch (error) {
    console.error('Error fetching files for station:', error);
    Toastify('error', 'Failed to fetch station files');
    throw error;
  }
}

/**
 * Create a new station
 * API Docs: POST /api/admin/buddies
 * Supports two modes:
 * - JSON: When avatar is a URL (send avatar in meta_config.avatar)
 * - Multipart: When avatar is a file (send file separately and omit meta_config.avatar)
 */
export async function createStation(payload: CreateStationPayload) {
  try {
    const user_id = getUserId();
    const { avatarFile, ...restPayload } = payload;

    // Check if we have an avatar file to upload
    if (avatarFile && avatarFile instanceof File) {
      // Multipart/form-data with file upload
      const form = new FormData();
      
      const cleanPayload = {
        ...restPayload,
        meta_config: {
          ...restPayload.meta_config,
        }
      };
      
      // Remove avatar from meta_config if it exists (backend will inject the uploaded URL)
      if (cleanPayload.meta_config?.avatar) {
        delete cleanPayload.meta_config.avatar;
      }
      
      // Remove empty parent_buddy_id
      if (cleanPayload.parent_buddy_id === '' || !cleanPayload.parent_buddy_id) {
        delete cleanPayload.parent_buddy_id;
      }
      
      // Filter out empty starter fields from base_prompts
      if (cleanPayload.meta_config?.base_prompts && Array.isArray(cleanPayload.meta_config.base_prompts)) {
        cleanPayload.meta_config.base_prompts = cleanPayload.meta_config.base_prompts.filter(
          (starter: any) => starter.text && starter.text.trim().length > 0
        );
      }
      
      form.append('buddy_payload', JSON.stringify(cleanPayload));
      form.append('avatar', avatarFile, avatarFile.name);

      const config: any = {
        params: { user_id },
        headers: {
          ...apiClient.defaults.headers.common,
          'Content-Type': undefined,
        },
        transformRequest: [(data: any) => data],
      };

      const response = await apiClient.post<StationWithChildren>(
        apiUrl('/api/admin/buddies'),
        form,
        config
      );
      Toastify('success', 'Station created with uploaded avatar');
      await logAdminAction(`Station "${payload.name}" created (with avatar)`, {
        url: apiUrl('/api/admin/buddies'),
        method: 'POST',
        status: 200,
        category: 'settings',
        action: 'create_station',
        stationId: (response.data as any)?.buddy_id,
        model: payload.default_model,
      });
      return response.data;
    } else {
      // Pure JSON with avatar URL (or no avatar)
      const cleanedPayload = { ...restPayload };
      if (cleanedPayload.parent_buddy_id === '' || !cleanedPayload.parent_buddy_id) {
        delete cleanedPayload.parent_buddy_id;
      }
      
      // Filter out empty starter fields from base_prompts
      if (cleanedPayload.meta_config?.base_prompts && Array.isArray(cleanedPayload.meta_config.base_prompts)) {
        cleanedPayload.meta_config.base_prompts = cleanedPayload.meta_config.base_prompts.filter(
          (starter: any) => starter.text && starter.text.trim().length > 0
        );
      }
      
      const response = await apiClient.post<StationWithChildren>(
        apiUrl('/api/admin/buddies'),
        cleanedPayload,
        {
          params: { user_id },
        }
      );
      Toastify('success', 'Station created');
      await logAdminAction(`Station "${payload.name}" created`, {
        url: apiUrl('/api/admin/buddies'),
        method: 'POST',
        status: 200,
        category: 'settings',
        action: 'create_station',
        stationId: (response.data as any)?.buddy_id,
        model: payload.default_model,
      });
      return response.data;
    }
  } catch (error: any) {
    console.error('Error creating station:', error);
    
    if (error.response) {
      const backendMessage = error.response.data?.detail || error.response.data?.message;
      if (backendMessage) {
        Toastify('error', `Failed to create station: ${backendMessage}`);
      } else {
        Toastify('error', `Failed to create station (${error.response.status})`);
      }
    } else {
      Toastify('error', 'Failed to create station');
    }
    await logAdminAction(
      `Failed to create station "${payload.name}": ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      { url: apiUrl('/api/admin/buddies'), method: 'POST', category: 'settings', action: 'create_station' },
      true
    );
    throw error;
  }
}

/**
 * Update existing station configuration
 * API Docs: PATCH /api/admin/buddies/{station_id}
 */
export async function updateStation(stationId: string, payload: UpdateStationPayload) {
  // Snapshot current station BEFORE updating for before -> after change logs.
  // Best-effort: uses a raw GET so it never triggers user-facing errors.
  let previousStation: any = null;
  try {
    const user_id = getUserId();
    const snapshot = await apiClient.get<StationWithChildren>(
      apiUrl(`/api/admin/buddies/${stationId}`),
      { params: { user_id } }
    );
    previousStation = snapshot.data;
  } catch (snapshotErr) {
    console.warn('Could not fetch previous station for change log:', snapshotErr);
  }

  try {
    const response = await apiClient.patch<StationWithChildren>(apiUrl(`/api/admin/buddies/${stationId}`), payload);
    Toastify('success', 'Station updated');
    const changedSpecs = STATION_FIELD_SPECS.filter(
      spec => (payload as any)?.[spec.key] !== undefined
    );
    const changes = previousStation
      ? diffFields(previousStation, payload as any, changedSpecs).map(truncateChange)
      : [];
    const changeSummary = changes
      .map(c => (c.added || c.removed ? c.label : `${c.label}: ${c.from} → ${c.to}`))
      .join('; ');
    await logAdminAction(
      `Station "${payload.name ?? previousStation?.name ?? stationId}" updated${
        changeSummary
          ? ` — ${changeSummary}`
          : changedSpecs.length
            ? ` (${changedSpecs.map(s => s.label).join(', ')})`
            : ''
      }`,
      {
        url: apiUrl(`/api/admin/buddies/${stationId}`),
        method: 'PATCH',
        status: 200,
        category: 'settings',
        action: 'update_station',
        stationId,
        changes,
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error updating station:', error);
    Toastify('error', 'Failed to update station');
    await logAdminAction(
      `Failed to update station ${stationId}: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      { url: apiUrl(`/api/admin/buddies/${stationId}`), method: 'PATCH', category: 'settings', action: 'update_station', stationId },
      true
    );
    throw error;
  }
}

/**
 * Create a new version under an existing parent station.
 * API Docs: POST /api/admin/buddies/versions
 * Supports two modes:
 * - JSON: When avatar is a URL (send avatar in meta_config.avatar)
 * - Multipart: When avatar is a file (send file separately and omit meta_config.avatar)
 */
export async function createStationVersion(payload: CreateStationVersionPayload) {
  try {
    const user_id = getUserId();
    const { avatarFile, ...restPayload } = payload;

    // Check if we have an avatar file to upload
    if (avatarFile && avatarFile instanceof File) {
      // Multipart/form-data with file upload
      const form = new FormData();
      
      const cleanPayload = {
        ...restPayload,
        meta_config: {
          ...restPayload.meta_config,
        }
      };
      
      // Remove avatar from meta_config if it exists (backend will inject the uploaded URL)
      if (cleanPayload.meta_config?.avatar) {
        delete cleanPayload.meta_config.avatar;
      }
      
      // Filter out empty starter fields from base_prompts
      if (cleanPayload.meta_config?.base_prompts && Array.isArray(cleanPayload.meta_config.base_prompts)) {
        cleanPayload.meta_config.base_prompts = cleanPayload.meta_config.base_prompts.filter(
          (starter: any) => starter.text && starter.text.trim().length > 0
        );
      }
      
      form.append('buddy_payload', JSON.stringify(cleanPayload));
      form.append('avatar', avatarFile, avatarFile.name);

      const config: any = {
        params: { user_id },
        headers: {
          ...apiClient.defaults.headers.common,
          'Content-Type': undefined,
        },
        transformRequest: [(data: any) => data],
      };

      const response = await apiClient.post<StationWithChildren>(
        apiUrl('/api/admin/buddies/versions'),
        form,
        config
      );
      Toastify('success', 'New station version created with uploaded avatar');
      await logAdminAction(`Station version created for "${payload.name}" (with avatar)`, {
        url: apiUrl('/api/admin/buddies/versions'),
        method: 'POST',
        status: 200,
        category: 'settings',
        action: 'create_station_version',
        parentStationId: payload.parent_agent_id,
      });
      return response.data;
    } else {
      // Pure JSON with avatar URL (or no avatar)
      const cleanedPayload = { ...restPayload };
      
      // Filter out empty starter fields from base_prompts
      if (cleanedPayload.meta_config?.base_prompts && Array.isArray(cleanedPayload.meta_config.base_prompts)) {
        cleanedPayload.meta_config.base_prompts = cleanedPayload.meta_config.base_prompts.filter(
          (starter: any) => starter.text && starter.text.trim().length > 0
        );
      }
      
      const response = await apiClient.post<StationWithChildren>(
        apiUrl('/api/admin/buddies/versions'),
        cleanedPayload,
        {
          params: { user_id },
        }
      );
      Toastify('success', 'New station version created');
      await logAdminAction(`Station version created for "${payload.name}"`, {
        url: apiUrl('/api/admin/buddies/versions'),
        method: 'POST',
        status: 200,
        category: 'settings',
        action: 'create_station_version',
        parentStationId: payload.parent_agent_id,
      });
      return response.data;
    }
  } catch (error: any) {
    console.error('Error creating station version:', error);
    
    if (error.response) {
      const backendMessage = error.response.data?.detail || error.response.data?.message;
      if (backendMessage) {
        Toastify('error', `Failed to create station version: ${backendMessage}`);
      } else {
        Toastify('error', `Failed to create station version (${error.response.status})`);
      }
    } else {
      Toastify('error', 'Failed to create station version');
    }
    await logAdminAction(
      `Failed to create station version for "${payload.name}": ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      { url: apiUrl('/api/admin/buddies/versions'), method: 'POST', category: 'settings', action: 'create_station_version' },
      true
    );
    throw error;
  }
}

/**
 * Activate or deactivate a specific station version.
 * POST /api/admin/buddies/versions/activate
 */
export async function toggleStationVersion(stationId: string, activate: boolean) {
  try {
    const user_id = getUserId();
    await apiClient.post(apiUrl('/api/admin/buddies/versions/activate'), {
      agent_id: stationId,
      activate,
      user_id,
    });
    Toastify('success', `Version ${activate ? 'activated' : 'deactivated'}`);
    await logAdminAction(`Station version ${activate ? 'activated' : 'deactivated'} (${stationId})`, {
      url: apiUrl('/api/admin/buddies/versions/activate'),
      method: 'POST',
      status: 200,
      category: 'settings',
      action: activate ? 'activate_station_version' : 'deactivate_station_version',
      stationId,
    });
  } catch (error) {
    console.error('Error toggling station version:', error);
    Toastify('error', `Failed to ${activate ? 'activate' : 'deactivate'} version`);
    throw error;
  }
}

/**
 * Activate or deactivate a specific file version for a station.
 * POST /api/admin/buddies/{station_id}/files/versions/activate
 */
export async function toggleFileVersion(stationId: string, fileId: string, activate: boolean) {
  try {
    await apiClient.post(apiUrl(`/api/admin/buddies/${stationId}/files/versions/activate`), {
      file_id: fileId,
      activate,
    });
    Toastify('success', `File version ${activate ? 'activated' : 'deactivated'}`);
    await logAdminAction(`Station file version ${activate ? 'activated' : 'deactivated'}`, {
      url: apiUrl(`/api/admin/buddies/${stationId}/files/versions/activate`),
      method: 'POST',
      status: 200,
      category: 'settings',
      action: activate ? 'activate_station_file' : 'deactivate_station_file',
      stationId,
      fileId,
    });
  } catch (error) {
    console.error('Error toggling file version:', error);
    Toastify('error', `Failed to ${activate ? 'activate' : 'deactivate'} file version`);
    throw error;
  }
}

/**
 * Fetch full details of a station
 * GET /api/admin/buddies/{station_id}
 */
export async function getStationById(stationId: string) {
  try {
    const user_id = getUserId();
    const response = await apiClient.get<StationWithChildren>(apiUrl(`/api/admin/buddies/${stationId}`), {
      params: { user_id },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching station details:', error);
    Toastify('error', 'Failed to fetch station details');
    throw error;
  }
}

export async function deleteStation(stationId: string): Promise<void> {
  try {
    const user_id = getUserId();
    await apiClient.delete(apiUrl(`/api/admin/buddies/${stationId}`), {
      params: { user_id },
    });
    Toastify('success', 'Station deleted');
    await logAdminAction(`Station deleted (${stationId})`, {
      url: apiUrl(`/api/admin/buddies/${stationId}`),
      method: 'DELETE',
      status: 200,
      category: 'settings',
      action: 'delete_station',
      stationId,
    });
  } catch (error: any) {
    console.error('Error deleting station:', error);
    Toastify('error', 'Failed to delete station');
    await logAdminAction(
      `Failed to delete station ${stationId}: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      { url: apiUrl(`/api/admin/buddies/${stationId}`), method: 'DELETE', category: 'settings', action: 'delete_station', stationId },
      true
    );
    throw error;
  }
}

/**
 * Force delete a parent station and ALL related data.
 * DELETE /api/admin/buddies/{station_id}/force
 */
export async function forceDeleteStation(stationId: string) {
  try {
    const user_id = getUserId();
    await apiClient.delete(apiUrl(`/api/admin/buddies/${stationId}/force`), {
      params: { user_id },
    });
    Toastify('success', 'Station and all related data deleted');
    await logAdminAction(`Station force-deleted with all related data (${stationId})`, {
      url: apiUrl(`/api/admin/buddies/${stationId}/force`),
      method: 'DELETE',
      status: 200,
      category: 'settings',
      action: 'force_delete_station',
      stationId,
    });
  } catch (error: any) {
    console.error('Error force deleting station:', error);
    Toastify('error', 'Failed to delete station and related data');
    await logAdminAction(
      `Failed to force-delete station ${stationId}: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      { url: apiUrl(`/api/admin/buddies/${stationId}/force`), method: 'DELETE', category: 'settings', action: 'force_delete_station', stationId },
      true
    );
    throw error;
  }
}

export async function uploadStationFiles(
  stationId: string,
  files: File[],
  version = 1
) {
  if (!files.length) throw new Error('No files provided');

  const form = new FormData();
  files.forEach((f) => {
    form.append('files', f);
  });

  const user_id = getUserId();

  try {
    const { data } = await apiClient.post<{uploaded: StationFile[], failed: any[]}>(
      apiUrl('/api/admin/files/upload-multiple'),
      form,
      {
        params: { station_id: stationId, version, user_id },
        headers: { 
          'Content-Type': undefined, // Let browser set multipart/form-data with boundary
        },
        transformRequest: [(data: any) => data],
      }
    );

    // Handle the new response structure with uploaded and failed arrays
    const uploadedFiles = data.uploaded || [];
    const failedFiles = data.failed || [];

    // The backend may not echo back the original file name/size. Patch them in for UI convenience.
    uploadedFiles.forEach((fileObj, idx) => {
      const original = files[idx];
      if (original) {
        if (!fileObj.file_name) fileObj.file_name = original.name;
        if (!fileObj.file_size) fileObj.file_size = original.size;
      }
    });

    // Show success toast with details about uploaded/failed files
    if (uploadedFiles.length > 0) {
      const successCount = uploadedFiles.length;
      Toastify('success', `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`);
      await logAdminAction(`${successCount} file(s) uploaded to station ${stationId}`, {
        url: apiUrl('/api/admin/files/upload-multiple'),
        method: 'POST',
        status: 200,
        category: 'settings',
        action: 'upload_station_files',
        stationId,
        uploadedCount: successCount,
        failedCount: failedFiles.length,
      });
    }
    
    if (failedFiles.length > 0) {
      Toastify('warning', `${failedFiles.length} file${failedFiles.length > 1 ? 's' : ''} failed to upload`);
    }
    
    // Return the uploaded files array to maintain backward compatibility
    return uploadedFiles;
  } catch (error: any) {
    // Handle error and show toast
    let errorMessage = 'Failed to upload files';
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    Toastify('error', errorMessage);
    throw error;
  }
}

/**
 * @deprecated Use uploadStationFiles instead which supports multiple file uploads.
 */
export async function uploadStationFile(stationId: string, file: File, version = 1) {
  try {
    const [single] = await uploadStationFiles(stationId, [file], version);
    return single;
  } catch (error: any) {
    // Error handling is already done in uploadStationFiles, just re-throw
    throw error;
  }
}

/**
 * Delete (disassociate) a file from a station
 */
export async function deleteStationFile(stationId: string, fileId: string) {
  try {
    await apiClient.delete(apiUrl(`/api/admin/files/${fileId}`));
    Toastify('success', 'File deleted');
    await logAdminAction(`Station file deleted (${fileId})`, {
      url: apiUrl(`/api/admin/files/${fileId}`),
      method: 'DELETE',
      status: 200,
      category: 'settings',
      action: 'delete_station_file',
      stationId,
      fileId,
    });
  } catch (error) {
    console.error('Error deleting station file:', error);
    Toastify('error', 'Failed to delete file');
    throw error;
  }
}

/**
 * Delete multiple files in a single call.
 * API Docs: DELETE /api/admin/files/bulk - Delete Multiple Files
 * Supply a JSON body like { "file_ids": ["file-id-1", "file-id-2", ...] }
 * Returns a summary with lists of deleted and failed IDs.
 */
export async function deleteMultipleStationFiles(fileIds: string[]) {
  if (!fileIds.length) throw new Error('No file IDs provided');
  
  try {
    const response = await apiClient.delete(apiUrl('/api/admin/files/delete-bulk'), {
      data: { file_ids: fileIds }
    });
    
    const deletedCount = fileIds.length;
    Toastify('success', `${deletedCount} file${deletedCount > 1 ? 's' : ''} deleted`);
    await logAdminAction(`${deletedCount} station file(s) deleted (bulk)`, {
      url: apiUrl('/api/admin/files/delete-bulk'),
      method: 'DELETE',
      status: 200,
      category: 'settings',
      action: 'bulk_delete_station_files',
      fileIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting multiple files:', error);
    Toastify('error', 'Failed to delete selected files');
    throw error;
  }
}

/**
 * Delete a specific version of a station
 */
export async function deleteStationVersion(stationId: string, version: number) {
  try {
    await apiClient.delete(apiUrl(`/buddies/versions/${stationId}/${version}`));
    Toastify('success', 'Station version deleted');
    await logAdminAction(`Station version ${version} deleted (${stationId})`, {
      url: apiUrl(`/buddies/versions/${stationId}/${version}`),
      method: 'DELETE',
      status: 200,
      category: 'settings',
      action: 'delete_station_version',
      stationId,
      version,
    });
  } catch (error) {
    console.error('Error deleting station version:', error);
    Toastify('error', 'Failed to delete station version');
    throw error;
  }
}

/**
 * Create a new version of an existing file for a station.
 * This endpoint expects multipart/form-data where the binary file is sent along
 * with the parent_file_id and an optional JSON encoded version_note.
 *
 * @param stationId      The station identifier
 * @param parentFileId   The file_id of the existing file we are versioning
 * @param file           The new file blob picked by the user
 * @param versionNote    Optional note describing the version changes
 */
export async function createFileVersion(
  stationId: string,
  parentFileId: string,
  file: File,
  versionNote?: { description?: string } | Record<string, unknown>
) {
  const form = new FormData();
  form.append('file', file);
  form.append('parent_file_id', parentFileId);
  if (versionNote) {
    form.append('version_note', JSON.stringify(versionNote));
  }

  const user_id = getUserId();
  
  try {
    const { data } = await apiClient.post(apiUrl(`/api/admin/buddies/${stationId}/files/versions`), form, {
      headers: { 
        'Content-Type': undefined, // Let browser set multipart/form-data with boundary
      },
      params: { user_id },
      transformRequest: [(data: any) => data],
    });
    Toastify('success', 'File version created');
    await logAdminAction(`File version created for station ${stationId}`, {
      url: apiUrl(`/api/admin/buddies/${stationId}/files/versions`),
      method: 'POST',
      status: 200,
      category: 'settings',
      action: 'create_station_file_version',
      stationId,
      parentFileId,
    });
    return data;
  } catch (error: any) {
    // Handle error and show toast
    let errorMessage = 'Failed to create file version';
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    Toastify('error', errorMessage);
    throw error;
  }
}

export interface ModelEntry {
  name: string;
  display_name: string;
  meta_config?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export async function getModels(): Promise<ModelEntry[]> {
  try {
    const response = await apiClient.get<ModelEntry[]>(apiUrl('/api/v1/models/'));
    return response.data;
  } catch (error) {
    console.error('Error fetching models:', error);
    Toastify('error', 'Failed to fetch available models');
    throw error;
  }
}

// ----------  Sort Order  ------------------------------------------------

export interface SortOrderItem {
  id: string;
  sort_order: number;
}

export interface SortOrderRequest {
  entity_type: 'station';
  items: SortOrderItem[];
}

export interface SortOrderResult {
  id: string;
  success: boolean;
  sort_order: number;
  error: string | null;
}

export interface SortOrderResponse {
  entity_type: 'station';
  total: number;
  updated: number;
  failed: number;
  results: SortOrderResult[];
  message: string;
}

/**
 * Update sort order for stations
 * PUT /api/v1/sort-order/
 */
export async function updateStationSortOrder(items: SortOrderItem[]): Promise<SortOrderResponse> {
  try {
    const payload: SortOrderRequest = {
      entity_type: 'station',
      items
    };
    
    const response = await apiClient.put<SortOrderResponse>(apiUrl('/api/v1/sort-order/'), payload);
    
    if (response.data.failed > 0) {
      Toastify('warning', `Updated ${response.data.updated} stations, ${response.data.failed} failed`);
    } else {
      Toastify('success', response.data.message);
    }
    await logAdminAction(`Station sort order updated (${response.data.updated} station(s))`, {
      url: apiUrl('/api/v1/sort-order/'),
      method: 'PUT',
      status: 200,
      category: 'settings',
      action: 'reorder_stations',
      updated: response.data.updated,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating station sort order:', error);
    const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update station order';
    Toastify('error', errorMessage);
    throw error;
  }
}
